import { useState, useRef } from 'react';
import type { InventoryItem } from '../../types';
import { StatusBadge } from '../ui/StatusBadge';
import { usePhotoUpload, usePhotoDelete } from '../../hooks/useInventory';

// Consistent color per team name (hash → 20-color palette)
const TEAM_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#0EA5E9', '#84CC16', '#F97316', '#14B8A6',
  '#6366F1', '#D97706', '#059669', '#DC2626', '#7C3AED',
  '#06B6D4', '#65A30D', '#FB923C', '#0D9488', '#A855F7',
];

export function teamColor(name: string): string {
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return TEAM_COLORS[hash % TEAM_COLORS.length];
}

/** Resize image via Canvas to max 400×533 JPEG @ 0.75 quality */
async function resizeImage(file: File): Promise<{ imageBase64: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX_W = 400;
      const MAX_H = 533;
      let { width, height } = img;
      if (width > MAX_W || height > MAX_H) {
        const ratio = Math.min(MAX_W / width, MAX_H / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not available')); return; }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
      const imageBase64 = dataUrl.split(',')[1];
      resolve({ imageBase64, contentType: 'image/jpeg' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

interface StickerCardProps {
  item: InventoryItem;
  onUpdateQuantity?: (stickerId: string, quantity: number) => void;
  /** When true, shows camera button for photo management */
  allowPhoto?: boolean;
}

export function StickerCard({ item, onUpdateQuantity, allowPhoto = false }: StickerCardProps) {
  const [imgError, setImgError] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const photoUpload = usePhotoUpload();
  const photoDelete = usePhotoDelete();

  const color = teamColor(item.nationalTeam);
  const isDuplicated = item.status === 'DUPLICADA';
  const isMissing = item.status === 'FALTA';

  const photoSrc = item.photoUrl ?? null;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { imageBase64, contentType } = await resizeImage(file);
      photoUpload.mutate({ stickerId: item.stickerId, imageBase64, contentType });
    } catch {
      // silently ignore – user can retry
    }
    // reset input so the same file can be re-selected
    e.target.value = '';
  }

  const isUploading = photoUpload.isPending;

  return (
    <div
      className={[
        'sticker-card flex flex-col overflow-hidden',
        isDuplicated ? 'ring-2 ring-amber-400 ring-offset-1' : '',
        isMissing ? 'opacity-60' : '',
      ].join(' ')}
    >
      {/* Team color strip */}
      <div className="h-1.5 flex-shrink-0" style={{ backgroundColor: color }} />

      {/* Image area */}
      <div className="relative">
        <span className="absolute top-1.5 left-1.5 z-10 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md leading-none">
          #{item.albumNumber ?? item.stickerId}
        </span>

        {isDuplicated && item.quantity > 1 && (
          <span className="absolute top-1.5 right-1.5 z-10 bg-amber-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md leading-none">
            +{item.quantity - 1}
          </span>
        )}

        {photoSrc && !imgError ? (
          <img
            src={photoSrc}
            alt={item.playerName}
            className="w-full object-cover"
            style={{ aspectRatio: '3/4' }}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className="w-full flex flex-col items-center justify-center gap-1"
            style={{ aspectRatio: '3/4', backgroundColor: color + '18' }}
          >
            <span className="font-display text-4xl font-bold" style={{ color: color + '80' }}>
              {item.albumNumber ?? item.stickerId}
            </span>
            <span className="text-[10px] text-gray-400 text-center px-2 leading-tight font-semibold">
              {item.nationalTeam}
            </span>
          </div>
        )}

        {/* Camera overlay button */}
        {allowPhoto && (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={isUploading}
            className="absolute bottom-1.5 right-1.5 z-10 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center text-sm transition-colors disabled:opacity-50"
            title={photoSrc ? 'Cambiar foto' : 'Agregar foto'}
          >
            {isUploading ? (
              <span className="animate-spin text-xs">○</span>
            ) : (
              '📷'
            )}
          </button>
        )}

        {/* Delete photo button */}
        {allowPhoto && photoSrc && !isUploading && (
          <button
            onClick={() => { setImgError(false); photoDelete.mutate(item.stickerId); }}
            disabled={photoDelete.isPending}
            className="absolute bottom-1.5 left-1.5 z-10 w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center text-xs transition-colors disabled:opacity-50"
            title="Eliminar foto"
          >
            ✕
          </button>
        )}

        {/* Hidden file input – capture=environment opens rear camera on mobile */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Info */}
      <div className="p-2.5 flex flex-col gap-1.5 flex-1">
        <div className="font-bold text-xs leading-tight truncate text-gray-800">
          {item.playerName}
        </div>
        <StatusBadge status={item.status} />

        {/* Quantity stepper */}
        {onUpdateQuantity && (
          <div className="flex items-center mt-0.5">
            <button
              onClick={() => onUpdateQuantity(item.stickerId, Math.max(0, item.quantity - 1))}
              className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-500 font-bold text-base flex items-center justify-center transition-colors"
              aria-label="Decrementar"
            >
              −
            </button>
            <span className="flex-1 text-center font-bold text-sm text-gray-700">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.stickerId, item.quantity + 1)}
              className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-green-100 hover:text-green-600 text-gray-500 font-bold text-base flex items-center justify-center transition-colors"
              aria-label="Incrementar"
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
